import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    titleId?: string
    align?: 'left' | 'center'
    tone?: 'light' | 'dark'
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    titleId,
    align = 'left',
    tone = 'light',
    className
}): ReactNode => {
    const isDark = tone === 'dark'

    return (
        <div className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center', className)}>
            <p
                className={cn(
                    'inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.2em]',
                    isDark ? 'text-yolk' : 'text-primary'
                )}
            >
                <span aria-hidden='true' className={cn('h-px w-6', isDark ? 'bg-yolk/70' : 'bg-primary/60')} />
                {eyebrow}
            </p>

            <h2
                id={titleId}
                className={cn(
                    'mt-4 text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl [&_em]:font-normal [&_em]:italic',
                    isDark ? '[&_em]:text-yolk' : '[&_em]:text-primary'
                )}
            >
                {title}
            </h2>

            {description && (
                <p className={cn('mt-5 text-lg leading-relaxed', isDark ? 'text-broth-foreground/75' : 'text-muted-foreground')}>
                    {description}
                </p>
            )}
        </div>
    )
}
